// PlainLanguageSummary — renders the include / exclude expression trees as
// human-readable text at the top of the build area, matching the mockup's
// "This audience includes people who…" banner.
//
// Each LEAF is rendered as a single chip containing the attribute label and
// its values formatted as natural language (e.g. "Kansas, Missouri, or
// Iowa", "25 to 65"). GROUPS render their children wrapped in literal "(" /
// ")" with the operator (AND / OR) styled in italic between siblings. The
// exclude expression, when present, is appended after "but not".
//
// PR 2b-3 ships a basic factual rendering — the mockup's prose-style "Live
// in <Kansas…>" would require per-attribute verb metadata that doesn't
// exist in the data dictionary. Easy to layer on later.

import { Fragment, useEffect, useRef, useState } from 'react';
import {
  formatModelScoreRange,
  isAudienceLeaf,
  isFileLeaf,
  isModelLeaf,
  isZipLeaf,
  isLeaf,
  isVariableLeaf,
} from '../../helpers/expressionTree';
import type {
  ExpressionGroup,
  ExpressionLeaf,
  ExpressionNode,
  VariableState,
} from '../../types/dataDictionary';

export type PlainLanguageSummaryProps = {
  expression: ExpressionNode | null;
  excludeExpression: ExpressionNode | null;
};

export default function PlainLanguageSummary({
  expression,
  excludeExpression,
}: PlainLanguageSummaryProps) {
  // Hide the banner entirely when there are no filters to describe — keeps
  // the empty state of the build area clean.
  if (!expression && !excludeExpression) return null;

  return (
    <div
      data-tour="plain-language-summary"
      className="bg-blue-50 rounded-lg px-3 py-2 mb-4 text-xs text-slate-700 leading-relaxed relative"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[9px] font-semibold tracking-wider text-slate-400 uppercase">
          This audience includes people who…
        </div>
        <CopyButton getText={() => collectSummaryText(expression, excludeExpression)} />
      </div>
      <div>
        {expression ? (
          // PR 2b-4: render the root expression "naked" — no outer parens.
          // Parens only appear around NESTED sub-groups (Screen 4 of mockup).
          <NodeText node={expression} isRoot />
        ) : (
          <span className="italic text-slate-400">no filters yet</span>
        )}
        {excludeExpression && (
          <>
            <span className="italic font-semibold text-slate-700"> but not </span>
            <NodeText node={excludeExpression} isRoot />
          </>
        )}
        <span>.</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recursive renderer
// ---------------------------------------------------------------------------

function NodeText({
  node,
  isRoot = false,
}: {
  node: ExpressionNode;
  isRoot?: boolean;
}) {
  if (isLeaf(node)) return <LeafChip leaf={node} />;
  return <GroupText group={node} isRoot={isRoot} />;
}

function GroupText({
  group,
  isRoot,
}: {
  group: ExpressionGroup;
  isRoot: boolean;
}) {
  const opLabel = group.type.toUpperCase();
  return (
    <>
      {!isRoot && <span>(</span>}
      {group.children.map((child, idx) => (
        <Fragment key={child.nodeId}>
          {idx > 0 && (
            <span className="italic font-semibold text-slate-700"> {opLabel} </span>
          )}
          {/* Children of the root are NOT root themselves — they nest. */}
          <NodeText node={child} isRoot={false} />
        </Fragment>
      ))}
      {!isRoot && <span>)</span>}
    </>
  );
}

function LeafChip({ leaf }: { leaf: ExpressionLeaf }) {
  // PR 2c: dispatch by leaf kind. Membership leaves have no `variable`
  // payload — render their name with an "audience" / "file" prefix.
  // ATAD-146: the text comes from leafChipText, the copy-to-clipboard
  // version, so the two can't drift apart (a kind added to one only used to
  // render blank in the other).
  return (
    <span className="inline-block bg-white border border-blue-200 rounded px-1.5 py-0 mx-0.5 text-[10px] text-slate-700">
      {leafChipText(leaf)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Leaf → natural-language formatting
// ---------------------------------------------------------------------------

/** Format an array of values as "a", "a and b", or "a, b, or c". */
function joinValuesNatural(values: string[]): string {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} or ${values[1]}`;
  const head = values.slice(0, -1).join(', ');
  return `${head}, or ${values[values.length - 1]}`;
}

/** Best-effort label for one leaf. */
function leafToText(variable: VariableState): string {
  const { attributeName, attrs, state } = variable;
  const componentType = attrs.componentType;

  // Range: `<name>: <min> to <max>`.
  if (componentType === 'range' || componentType === 'float-range') {
    if (state && state.min !== undefined && state.max !== undefined) {
      return `${attributeName}: ${state.min} to ${state.max}`;
    }
    return attributeName;
  }

  // Multi-select families: list values as natural language.
  if (
    componentType === 'multi-select' ||
    componentType === 'multi-select-options' ||
    componentType === 'multi-select-range'
  ) {
    const values = state && Array.isArray(state.values) ? state.values : [];
    // Each entry is either a string or a { value, label } object — normalize.
    const labels = values
      .map((v: any) => (typeof v === 'string' ? v : v?.label ?? v?.value ?? ''))
      .filter(Boolean);
    if (labels.length === 0) return attributeName;
    return `${attributeName}: ${joinValuesNatural(labels)}`;
  }

  // Boolean / unknown: just the name (the act of including the leaf already
  // implies the boolean is true).
  return attributeName;
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard
// ---------------------------------------------------------------------------
// `collectSummaryText` mirrors the JSX rendering: walks the trees and
// returns a plain-text version suitable for the clipboard. Kept in lockstep
// with NodeText / GroupText / LeafChip — if the visual rendering rules
// change, update both. (Alternative: rip text from a DOM ref. This is more
// reliable because it doesn't depend on the visual escapes / spacing that
// React inserts between elements.)

export function collectSummaryText(
  expression: ExpressionNode | null,
  excludeExpression: ExpressionNode | null,
): string {
  const include = expression ? nodeToText(expression, true) : 'no filters yet';
  const exclude = excludeExpression
    ? ` but not ${nodeToText(excludeExpression, true)}`
    : '';
  return `This audience includes people who… ${include}${exclude}.`;
}

function nodeToText(node: ExpressionNode, isRoot: boolean): string {
  if (isLeaf(node)) return leafChipText(node);
  return groupToText(node, isRoot);
}

function groupToText(group: ExpressionGroup, isRoot: boolean): string {
  const op = group.type.toUpperCase();
  const inner = group.children
    .map((c) => nodeToText(c, false))
    .join(` ${op} `);
  return isRoot ? inner : `(${inner})`;
}

function leafChipText(leaf: ExpressionLeaf): string {
  if (isAudienceLeaf(leaf)) {
    return leaf.name ? `audience: ${leaf.name}` : 'audience';
  }
  if (isFileLeaf(leaf)) {
    return leaf.name ? `file: ${leaf.name}` : 'file';
  }
  if (isZipLeaf(leaf)) {
    return leaf.name ? `ZIP codes: ${leaf.name}` : 'ZIP codes';
  }
  if (isModelLeaf(leaf)) {
    // ATAD-146: e.g. "model: Man City fans (score 5–1)". The range is the
    // filter, so it is always shown.
    const range = `(score ${formatModelScoreRange(leaf.scoreMin, leaf.scoreMax)})`;
    return leaf.name ? `model: ${leaf.name} ${range}` : `model ${range}`;
  }
  if (isVariableLeaf(leaf)) {
    return leafToText(leaf.variable);
  }
  return '';
}

// ---------------------------------------------------------------------------
// CopyButton — top-right icon. Shows a tooltip on hover, swaps to a
// "Copied!" affordance for 1.5s after a successful copy.
// ---------------------------------------------------------------------------

type CopyButtonProps = {
  getText: () => string;
};

function CopyButton({ getText }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending revert timer when the component unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = async () => {
    const text = getText();
    try {
      // navigator.clipboard is the modern, async clipboard API. Falls back
      // silently on unsupported browsers — copy-button-not-working is a
      // graceful degradation, not a crash.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback: hidden textarea + execCommand('copy').
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Silent fail; clipboard permissions can be denied (e.g. in iframes).
      console.warn('[PlainLanguageSummary] copy failed', err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Copy summary to clipboard"
      data-tour="plain-language-summary-copy"
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className={[
        'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors',
        copied
          ? 'text-teal-700 bg-teal-50'
          : 'text-slate-500 hover:text-slate-800 hover:bg-blue-100',
      ].join(' ')}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
      <span>{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  );
}

function CopyIcon() {
  // Two-stacked-rectangles "copy" glyph. 12×12 inline SVG keeps the button
  // chrome small and matches the banner's tight typography.
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="5"
        y="5"
        width="9"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 8.5l3.5 3.5L13 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
